// @greenlight-spec: 26-versioning-update-strategy.md
// @greenlight-spec: 15-database-schema.md#update_runs
// @greenlight-spec: 16-api-conventions.md
import { z } from 'zod';
/**
 * Total transcript bytes kept per run. The updater's capture bounds itself to
 * exactly this, trimming from the front, so a stored transcript sitting on the
 * limit is one that dropped its earliest output — the only record that the trim
 * happened. Read by both the updater that writes the capture and the control
 * plane that reports it, so the two cannot drift apart.
 */
export const TRANSCRIPT_LIMIT_BYTES = 2 * 1024 * 1024;
export const UPDATE_RUN_MODES = ['apply', 'canary'];
export const UPDATE_RUN_TRIGGERS = ['cron', 'manual'];
export const UPDATE_RUN_OUTCOMES = [
    'applied',
    'up_to_date',
    'canary_plan_only',
    'failed',
    'unreported',
    'skipped',
    'deferred_validation_in_flight',
];
export const UPDATE_PHASES = ['check', 'preflight', 'apply', 'smoke_test', 'finalize'];
export const UPDATE_PHASE_STATUSES = ['started', 'succeeded', 'failed', 'skipped'];
/** The reason prefix an applied run carries when one of the installer's closing checks did not pass. */
export const POST_APPLY_CHECK_FAILED_REASON = 'post_apply_check_failed';
/**
 * Whether the run applied its release *and* failed one of the installer's
 * closing checks. The release is running, so this is not a failed update.
 */
export function isPostApplyCheckFailure(run) {
    return (run.outcome === 'applied' && (run.reason?.startsWith(POST_APPLY_CHECK_FAILED_REASON) ?? false));
}
/**
 * The runs whose transcript is sent to the fleet as a failure log: a run that
 * stopped `failed`, and an apply whose closing check failed. A run still going,
 * a clean apply, an up-to-date check, a preview, and an unreported Job never
 * match; an unreported Job wrote no transcript to send.
 */
export function isFailedUpdaterRun(run) {
    return run.outcome === 'failed' || isPostApplyCheckFailure(run);
}
/**
 * One row of the Updater card's Runs list. A `null` outcome is a run that has
 * not stamped its finish — in flight, or killed before it could — and stays
 * distinct from every recorded outcome, including `unreported`.
 *
 * `reason` is one bounded code from the updater's own vocabulary, never command
 * output; the transcript is where output lives.
 */
export const UpdateRunSummarySchema = z.object({
    id: z.string().uuid(),
    mode: z.enum(UPDATE_RUN_MODES),
    trigger: z.enum(UPDATE_RUN_TRIGGERS),
    outcome: z.enum(UPDATE_RUN_OUTCOMES).nullable(),
    reason: z.string().nullable(),
    target_version: z.string().nullable(),
    previous_version: z.string().nullable(),
    job_name: z.string().nullable(),
    started_at: z.string().datetime({ offset: true }),
    finished_at: z.string().datetime({ offset: true }).nullable(),
    /** Null while the run is still open — there is no duration to report yet. */
    duration_ms: z.number().int().nullable(),
});
/** One `(run_id, phase)` row of the run's coarse timeline. */
export const UpdateRunPhaseSchema = z.object({
    phase: z.enum(UPDATE_PHASES),
    status: z.enum(UPDATE_PHASE_STATUSES),
    started_at: z.string().datetime({ offset: true }),
    finished_at: z.string().datetime({ offset: true }).nullable(),
    duration_ms: z.number().int().nullable(),
    /** `details.reason`, the bounded code the phase recorded. */
    reason: z.string().nullable(),
    /** The rest of `details` — bounded codes the updater sanitized on write. */
    details: z.record(z.union([z.string(), z.number(), z.boolean()])).nullable(),
});
/**
 * What the stored transcript is, without any of it: enough for the viewer to
 * say "2 MB, trimmed to its tail" before fetching a byte.
 */
export const UpdateRunTranscriptMetaSchema = z.object({
    chunk_count: z.number().int(),
    size_bytes: z.number().int(),
    /** Whether the capture dropped its earliest output to fit the limit. */
    truncated: z.boolean(),
});
export const UpdateRunDetailSchema = UpdateRunSummarySchema.extend({
    manifest_sha256: z.string().nullable(),
    /**
     * The non-secret install-input snapshot the run replayed plus the flags the
     * updater added itself. Null on a run that never got far enough to record
     * one, and on every synthesized `unreported` run.
     */
    parameters: z.record(z.string()).nullable(),
    phases: z.array(UpdateRunPhaseSchema),
    transcript: UpdateRunTranscriptMetaSchema,
});
/**
 * Whether the install updates itself, and what it will do next. Every field is
 * nullable for one honest reason or another: the cluster read is unavailable
 * off-cluster, and an install that never enabled the updater has expressed no
 * posture at all.
 */
export const UpdaterPostureSchema = z.object({
    /**
     * Whether the updater CronJob exists. `null` when the control plane cannot
     * read the namespace at all — distinct from a confident "no CronJob", which
     * is how a disabled install reads (docs/26 has no `suspend` state).
     */
    updates_enabled: z.boolean().nullable(),
    /**
     * Why `updates_enabled` is null, when the reason is known: the apiserver
     * refused the control plane's own ServiceAccount token (HTTP 401). The
     * kubelet reissues the token on its own, so the state clears without a
     * restart. Absent when the read succeeded or failed for another reason.
     */
    cluster_credentials_rejected: z.boolean().optional(),
    /** The CronJob's own schedule expression and IANA zone. */
    schedule: z.string().nullable(),
    time_zone: z.string().nullable(),
    /** Computed from `schedule` in `time_zone`; null when either is unreadable. */
    next_run_at: z.string().datetime({ offset: true }).nullable(),
    last_scheduled_at: z.string().datetime({ offset: true }).nullable(),
    include_prereleases: z.boolean().nullable(),
    installed_version: z.string().nullable(),
    /**
     * Whether `installer` or `updater` put this release on the cluster. A manual
     * installer rerun leaves no run record, so naming the source is what keeps
     * that gap explicit instead of mysterious.
     */
    deployment_source: z.enum(['updater', 'installer', 'unknown']),
    /** The newest release the last check saw, and when that check ran. */
    newest_release_seen: z.string().nullable(),
    last_check_at: z.string().datetime({ offset: true }).nullable(),
    /** Null when either version is unknown — never a guess. */
    up_to_date: z.boolean().nullable(),
});
/**
 * A page of the stored transcript: the joined text of the chunks this page
 * covers, plus the same metadata the detail carries so a viewer opened straight
 * on the log can label what it is showing. The page extends the canonical
 * `{ items, next_cursor }` shape with `text` rather than an array of lines —
 * chunk boundaries are a storage detail and splitting on newlines would invent
 * a row per line for a two-megabyte capture.
 */
export const UpdateRunLogPageSchema = z.object({
    text: z.string(),
    next_cursor: z.string().nullable(),
    transcript: UpdateRunTranscriptMetaSchema,
});
//# sourceMappingURL=updater-runs.js.map